// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import moment from 'moment'
import { convertBetweenMandFt } from 'helpers/unitConversion'
import getAntennaPatternInfo from './getAntennaPatternInfo'
import { patternSet } from './initialValues'
import {
  castToString,
  ifEntityGetId,
  ifObjectGetValue,
} from 'helpers/reduxFormFunctions'

const formatAntennaDataForDetails = (antennaSpec, patterns, unitType) => {
  // deconstruct antennaSpec
  const {
    antenna,
    antennaCode,
    antennaPattern,
    antennaSpecId,
    associatedAntennaSpec,
    compliance,
    createTimestamp,
    createUser,
    feedDir,
    frequencyRange,
    lastModifiedTimestamp,
    lastModifiedUser,
    modifiedAntennaSpec,
    singlePolarization,
    status,
  } = antennaSpec

  // deconstruct antenna
  const {
    antennaApplications,
    antennaId,
    antennaFamily,
    classification,
    company: { companyId, companyName },
    modelNumber,
    polarizationCapability,
  } = antenna

  const beamwidth = castToString(antennaSpec.beamwidth)
  const beamwidth15db = castToString(antennaSpec.beamwidth15db)
  const classificationId = ifEntityGetId(classification)
  const complianceId = ifEntityGetId(compliance)
  const fbRatio = castToString(antennaSpec.fbRatio)
  const feedDirId = ifEntityGetId(feedDir)
  const feedFlangeDiameter = castToString(antennaSpec.feedFlangeDiameter)
  const gain = castToString(antennaSpec.gain)
  const hiFrequency = castToString(frequencyRange.hiFrequency)
  const loFrequency = castToString(frequencyRange.loFrequency)
  const polarizationCapabilityId = ifEntityGetId(polarizationCapability)
  const singlePolarizationId = ifEntityGetId(singlePolarization)
  const statusId = ifEntityGetId(status)
  const subreflectorDiameter = castToString(antennaSpec.subreflectorDiameter)

  // antennaPatternInfo is a redux-form friendly set of antenna pattern info
  const antennaPatternInfo = getAntennaPatternInfo({
    antennaPattern,
    patterns,
    includeIds: true,
  })

  const createInfo = {
    timestamp: moment(createTimestamp).format('YYYY/MM/DD'),
    user: ifObjectGetValue(createUser, 'userName'),
  }

  const lastModifiedInfo = {
    timestamp: moment(lastModifiedTimestamp).format('YYYY/MM/DD'),
    user: ifObjectGetValue(lastModifiedUser, 'userName'),
  }

  const famInfo = (
    antennaFamily
      ? {
        antennaFamily: antennaFamily.antennaFamily,
        antennaFamilyId: antennaFamily.antennaFamilyId,
      } : {
        antennaFamily: '',
        antennaFamilyId: '',
      }
  )

  // associated antenna - in basic info
  const associatedAntennaInfo = (
    associatedAntennaSpec
      ? {
        associatedAntennaSpec: associatedAntennaSpec.antennaCode,
        associatedAntennaSpecId: associatedAntennaSpec.antennaSpecId,
      } : {
        associatedAntennaSpec: '',
        associatedAntennaSpecId: '',
      }
  )

  // rpe reference antenna - in pattern info
  // none of these values are required so they might be null
  const modifiedAntennaInfo = (
    modifiedAntennaSpec
      ? {
        modifiedAntennaSpec: modifiedAntennaSpec.antennaCode || '',
        modifiedAntennaSpecId: modifiedAntennaSpec.antennaSpecId || '',
      } : {
        modifiedAntennaSpec: '',
        modifiedAntennaSpecId: '',
      }
  )

  // in the DB this value can be one to many.
  // however for the current iteration,
  // there will be only one per antenna spec.
  const antennaApplicationId = (
    antennaApplications.length
      ? `${antennaApplications[0].antennaApplicationId}`
      : ''
  )

  // now, if there is a patPol identified by the API but this specific
  // antenna doesn't have anything assigned for a specified pattern,
  // give this patPol the default pattern set of 15 empty rows.
  Object.keys(patterns).map(k => {
    if (!patterns[k].length) {
      patterns[k] = patternSet
    }
  })

  // then get the key for the displayed pattern set based on the first patPol identified from the API.
  // eg. 'VV'
  const patternsSetDisplayed = Object.keys(patterns)[0]

  const diameter = castToString(
    unitType === 'SI'
      ? antenna.diameter
      : convertBetweenMandFt(antenna.diameter, 'US')
  )
  const diameterUnit = unitType

  // this will be used as initialValues for the form.
  // the ones which are converted to strings will be used by select
  // or checkbox groups. because redux form converts values to strings,
  // this ensures that components which will take form data as props
  // do no need to support the value as an int during the first rendering cycle.
  return {
    ...antennaPatternInfo,
    ...associatedAntennaInfo,
    ...famInfo,
    ...modifiedAntennaInfo,
    antennaApplicationId,
    antennaCode,
    antennaId,
    antennaSpecId,
    beamwidth,
    beamwidth15db,
    classificationId,
    company: companyName,
    companyId,
    complianceId,
    createInfo,
    diameter,
    diameterUnit,
    fbRatio,
    feedDirId,
    feedFlangeDiameter,
    gain,
    hiFrequency,
    lastModifiedInfo,
    loFrequency,
    modelNumber,
    patterns,
    patternsSetDisplayed,
    polarizationCapabilityId,
    singlePolarizationId,
    statusId,
    subreflectorDiameter,
  }
}

export default formatAntennaDataForDetails
